import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { api, inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Navbar } from "@/components/Navbar";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("checking");
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("invalid");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          setOrder(data.order);
          clear();
          return;
        }
      } catch {}
      attempts += 1;
      if (attempts < 15) timer = setTimeout(poll, 2000);
      else setState("pending");
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <main className="min-h-screen flex flex-col" data-testid="payment-success-page">
      <Navbar />
      <div className="flex-1 flex items-center justify-center relative overflow-hidden px-6 pt-20">
        <div className="spotlight absolute inset-0" aria-hidden="true" />
        <motion.div initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="relative z-10 w-full max-w-2xl text-center py-24">
          {state === "checking" && (
            <div data-testid="payment-checking">
              <Loader2 size={32} className="animate-spin text-gold mx-auto" />
              <p className="font-serif italic text-3xl text-zinc-600 mt-8">Confirming your payment…</p>
            </div>
          )}
          {state === "paid" && (
            <div data-testid="payment-confirmed">
              <div className="w-16 h-16 border border-gold flex items-center justify-center mx-auto">
                <Check size={26} className="text-gold" />
              </div>
              <h1 className="font-serif font-medium text-5xl text-zinc-900 mt-10">Yours, <em className="text-gold">shortly</em>.</h1>
              <p className="text-zinc-500 mt-6">Payment received. Your order is confirmed and being prepared.</p>
              {order && (
                <div className="mt-10 border border-zinc-200 bg-white p-8 text-left" data-testid="order-summary">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-5">Order #{order.id.slice(0, 8).toUpperCase()}</p>
                  <div className="space-y-3">
                    {order.items.map((i) => (
                      <div key={i.product_id} className="flex justify-between text-sm">
                        <span className="text-zinc-600">{i.name} × {i.quantity}</span>
                        <span className="text-zinc-500">{inr(i.price * i.quantity)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-zinc-200 mt-5 pt-5 flex justify-between">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Total Paid</span>
                    <span className="font-serif text-2xl text-gold">{inr(order.total)}</span>
                  </div>
                </div>
              )}
              <Link to="/shop" data-testid="continue-shopping-link" className="inline-block mt-10 bg-gold text-white text-xs uppercase tracking-[0.3em] px-10 py-4 hover:bg-champagne transition-colors duration-300">
                Continue Shopping
              </Link>
            </div>
          )}
          {(state === "pending" || state === "invalid") && (
            <div data-testid="payment-pending">
              <h1 className="font-serif font-medium text-4xl text-zinc-900">Still confirming…</h1>
              <p className="text-zinc-500 mt-6">Your payment is being processed. If you were charged, your order will appear shortly.</p>
              <Link to="/shop" className="inline-block mt-10 text-xs uppercase tracking-[0.3em] text-gold border-b border-gold/40 pb-1">
                Back to the Collection
              </Link>
            </div>
          )}
        </motion.div>
      </div>
    </main>
  );
}
